(() => {
  if (window.RwadyCheckoutInformationLoaded) return;
  window.RwadyCheckoutInformationLoaded = true;

  const root = () => window.Shopify?.routes?.root || '/';
  const page = () => document.querySelector('[data-rwady-checkout-information]');
  const otpState = { challengeId: '', phone: '', mode: 'checkout', resolve: null, reject: null, sending: false, verifying: false, checkoutStarted: false, resendTimer: 0, authorization: null, authorizationPhone: '', receiptUploading: false, receiptUploadPromise: null, receiptStoredForAuthorization: '', receiptNeedsUpload: false };
  const accountClient = () => {
    const container = page();
    if (!window.RwadyAccountApi || !container?.dataset.accountApiBase) throw checkoutFailure('authorize', 'ACCOUNT_API_UNAVAILABLE');
    return window.RwadyAccountApi.createClient({ baseUrl: container.dataset.accountApiBase });
  };
  const paymentFollowUp = Object.freeze({
    cash_on_delivery: false,
    electronic_transfer: true,
    installment: true,
    online: true
  });
  const paymentPromptEvents = new WeakSet();
  const openMissingPaymentPicker = (container, event) => {
    const select = container?.querySelector('[data-rwady-field="payment"]');
    if (!select || select.value.trim()) return false;
    const picker = select.closest('[data-rwady-payment-picker]');
    const trigger = picker?.querySelector('[data-rwady-payment-trigger]');
    const menu = picker?.querySelector('[data-rwady-payment-menu]');
    if (trigger && menu && trigger.getClientRects().length) {
      paymentPromptEvents.add(event);
      menu.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      trigger.focus({ preventScroll: true });
      picker.scrollIntoView({ block: 'center', behavior: 'instant' });
    } else {
      select.scrollIntoView({ block: 'center', behavior: 'instant' });
      select.focus({ preventScroll: true });
      try { select.showPicker?.(); } catch (_) { /* Native picker may be unavailable. */ }
    }
    return true;
  };
  const selectedPaymentMethod = (container) => {
    const method = cleanCheckoutValue(container.querySelector('[data-rwady-field="payment"]')?.value);
    if (!Object.prototype.hasOwnProperty.call(paymentFollowUp, method)) {
      throw checkoutFailure('paymentMethod', method ? 'INVALID_PAYMENT_METHOD' : 'PAYMENT_METHOD_REQUIRED');
    }
    return method;
  };
  const appendPaymentPricingAttributes = (container, attributes, loyaltyIqd = 0) => {
    const storefront = window.RwadyStorefrontCart;
    if (!storefront) throw new Error('Storefront cart client is unavailable');
    const paymentMethod = container.querySelector('[data-rwady-field="payment"]')?.value.trim() || '';
    const summary = container.querySelector('[data-rwady-cart-shipping]');
    [...storefront.INSTALLMENT_PRICING_KEYS, ...storefront.ONLINE_PRICING_KEYS].forEach((key) => { attributes[key] = ''; });
    const pricing = storefront.paymentPricingAttributes({
      method: paymentMethod,
      cardDetail: container.querySelector('[data-payment-panel="online"] [data-rwady-field="payment_detail"]')?.value || '',
      baseCents: Math.max(0, Number(summary?.dataset.cartTotalCents) - loyaltyIqd * 100),
      installmentTotalCents: loyaltyIqd ? Math.round(Math.max(0, Number(summary?.dataset.cartTotalCents) - loyaltyIqd * 100) * 1.21052565) : summary?.dataset.installmentTotalCents,
      localCardRate: summary?.dataset.localCardRate,
      internationalCardRate: summary?.dataset.internationalCardRate,
      shippingIqd: shippingAmountIqd(container)
    });
    if (!pricing) throw new Error('Payment pricing is incomplete');
    Object.assign(attributes, pricing);
  };
  const appendStandardPaymentAttributes = (container, attributes) => {
    const payment = container.querySelector('[data-rwady-field="payment"]');
    const method = selectedPaymentMethod(container);
    const label = payment.options[payment.selectedIndex]?.textContent.trim() || method;
    attributes.pre_checkout_payment_method = method;
    attributes.pre_checkout_payment_method_label = label;
    attributes.pre_checkout_payment_follow_up = String(paymentFollowUp[method]);
  };
  const cleanCheckoutValue = (value) => String(value || '').trim().replace(/\s+/g, ' ');
  const checkoutFailure = (stage, code, details = {}) => {
    const failure = new Error(code);
    failure.code = code;
    failure.diagnostics = { stage, code, ...details };
    return failure;
  };
  const shippingAmountIqd = (container) => {
    const summary = container.querySelector('[data-rwady-cart-shipping]');
    const price = window.RwadyCartShipping?.getPrice?.(summary);
    if (Number.isFinite(price) && price >= 0) return Math.round(price);
    const visible = cleanCheckoutValue(summary?.querySelector('[data-rwady-shipping-price]')?.textContent);
    const visibleAmount = Number(visible.replace(/[^0-9]/g, ''));
    throw checkoutFailure('shipping', visibleAmount > 0 ? 'SHIPPING_VISIBLE_AMOUNT_UNAVAILABLE' : 'SHIPPING_AMOUNT_UNAVAILABLE');
  };
  const safeFailureStage = (value) => /^[A-Za-z0-9_]+$/.test(String(value || '')) ? String(value) : 'checkoutFlow';
  const safeFailureCode = (value, fallback) => /^[A-Z0-9_]+$/.test(String(value || '')) ? String(value) : fallback;
  const showCheckoutFailure = (output, exception) => {
    if (!output) return;
    const diagnostics = exception?.diagnostics || {};
    const stage = safeFailureStage(diagnostics.stage);
    const code = safeFailureCode(exception?.code || diagnostics.code, 'CHECKOUT_FLOW_FAILED');
    output.dataset.failureStage = stage;
    output.dataset.failureCode = code;
    output.dataset.httpStatus = Number.isFinite(Number(diagnostics.status)) ? String(Number(diagnostics.status)) : '';
    output.dataset.contentType = cleanCheckoutValue(diagnostics.contentType);
    output.dataset.jsonValid = typeof diagnostics.jsonValid === 'boolean' ? String(diagnostics.jsonValid) : '';
    output.dataset.requestIdPresent = String(Boolean(diagnostics.requestId));
    output.dataset.errorName = cleanCheckoutValue(diagnostics.errorName);
    output.dataset.fetchFailureType = cleanCheckoutValue(diagnostics.failureType);
    output.dataset.graphqlErrorCodes = (diagnostics.graphQLErrors || []).map((item) => safeFailureCode(item.code, 'UNKNOWN')).join(',');
    output.dataset.userErrorCodes = (diagnostics.userErrors || []).map((item) => safeFailureCode(item.code, 'UNKNOWN')).join(',');
    output.dataset.warningCodes = (diagnostics.warnings || []).map((item) => safeFailureCode(item.code, 'UNKNOWN')).join(',');
    const sessionChanged = code === 'ACCOUNT_SESSION_CHANGED' || code === 'ADDRESS_RESELECTION_REQUIRED';
    const general = sessionChanged
      ? (document.documentElement.dir === 'rtl' ? 'تغيّر الحساب، اختر عنوان التوصيل مجددًا.' : 'The account changed. Choose the delivery address again.')
      : (document.documentElement.dir === 'rtl' ? 'تعذر حفظ بيانات التوصيل. حاول مرة أخرى.' : 'Unable to save delivery information. Please try again.');
    output.hidden = false;
    const messages = {
      INVALID_PHONE: checkoutPhoneError(page()?.querySelector('[data-rwady-field="phone"]')?.value),
      CHECKOUT_NOT_ENABLED: nativeText('خدمة تأكيد الطلب غير متاحة حالياً. يرجى المحاولة لاحقاً.', 'Order confirmation is currently unavailable. Please try again later.'),
      CHECKOUT_UNAVAILABLE: nativeText('لم نتمكن من تأكيد نتيجة الطلب. اضغط تأكيد الطلب مرة أخرى للتحقق من نفس الطلب.', 'We could not confirm the order result. Press Confirm order again to check the same request.'),
      ORDER_NOT_CONFIRMED: nativeText('لم يصل تأكيد الطلب بعد. أعد المحاولة للتحقق من نفس الطلب.', 'Order confirmation has not arrived. Retry to check the same request.'),
      REQUEST_IN_PROGRESS: nativeText('توجد محاولة تأكيد قيد المتابعة. أبقِ السلة والعنوان كما هما وأعد المحاولة.', 'An order confirmation is in progress. Keep the cart and address unchanged and retry.'),
      REVIEW_PENDING: nativeText('طلب مراجعة التوصيل قيد المتابعة. انتظر رد المبيعات ثم حدّث حالة المراجعة.', 'The delivery review is pending. Wait for the sales team, then check its status.'),
      REVIEW_REJECTED: nativeText('لم تُعتمد مراجعة التوصيل. اطلب مراجعة جديدة أو تواصل مع فريق المبيعات.', 'The delivery review was declined. Request a new review or contact sales.'),
      UNSUPPORTED_SELLING_PLAN: nativeText('أحد المنتجات يحتاج ترتيبات طلب خاصة. تواصل مع فريق المبيعات لإكمال الطلب.', 'An item requires special ordering arrangements. Contact sales to complete your order.'),
      PRICING_UNAVAILABLE: nativeText('تعذر تحديث سعر الطلب حالياً. حاول مرة أخرى.', 'The order price could not be updated. Please retry.'),
      SHIPPING_UNAVAILABLE: nativeText('تعذر حساب أجرة التوصيل. اطلب مراجعة التوصيل.', 'Delivery could not be calculated. Request a delivery review.'),
      SHOPIFY_REJECTED: nativeText('تعذر تأكيد الطلب. راجع المنتجات والكميات ومعلومات التوصيل.', 'The order could not be confirmed. Review the items, quantities and delivery details.'),
      REVIEW_NOT_ACCEPTED: nativeText('حدّث حالة مراجعة التوصيل ثم أكّد الطلب.', 'Refresh the delivery review status, then confirm your order.'),
      REVIEW_STALE: nativeText('تغيّرت السلة أو معلومات التوصيل. اطلب مراجعة جديدة.', 'The cart or delivery details changed. Request a new review.'),
      PRICE_CHANGED: nativeText('تغيّر سعر الطلب. حدّث الصفحة وراجع المبلغ قبل التأكيد.', 'The order price changed. Refresh and review the amount before confirming.'),
      SHIPPING_CHANGED: nativeText('تغيّرت أجرة التوصيل. حدّث الصفحة وراجع المبلغ.', 'The delivery price changed. Refresh and review the amount.'),
      OUT_OF_STOCK: nativeText('بعض المنتجات غير متوفرة بالكمية المطلوبة. راجع السلة.', 'Some items are unavailable in the requested quantity. Review your cart.'),
      INVALID_AUTHORIZATION: nativeText('انتهت صلاحية التحقق. اضغط مرة أخرى للتحقق من رقمك.', 'Verification expired. Press again to verify your phone.'),
      CHECKOUT_STORAGE_UNAVAILABLE: nativeText('فعّل حفظ بيانات الموقع في المتصفح حتى نتمكن من حفظ طلبك ومنع تكراره.', 'Allow this site to store browser data so your order can be saved without duplicates.')
    };
    output.textContent = messages[code] || general;
  };
  const checkoutAlternatePhone = (container, primaryPhone) => {
    const raw = cleanCheckoutValue(container.querySelector('[data-rwady-alternate-phone]')?.value);
    const resolved = window.RwadyAccountApi?.alternatePhone?.(raw, primaryPhone);
    if (!resolved || resolved.state === 'invalid') throw checkoutFailure('contactPhone', 'INVALID_CONTACT_PHONE');
    return resolved.phone;
  };
  const storefrontCustomer = (container, contactPhone = '') => {
    const storefront = window.RwadyStorefrontCart;
    if (!storefront) throw new Error('Storefront cart client is unavailable');
    const value = (name) => cleanCheckoutValue(container.querySelector(`[data-rwady-field="${name}"]`)?.value);
    const current = {
      name: value('name'),
      phone: value('phone'),
      email: value('email'),
      province: value('province'),
      address: value('address')
    };
    const select = container.querySelector('[data-rwady-saved-address]');
    const option = select?.options?.[select.selectedIndex];
    const hasSelectedAddress = Boolean(option?.value);
    const selectedAddressUnchanged = hasSelectedAddress
      && comparableAddress(savedAddressValue(option)) === comparableAddress(current.address)
      && (!cleanCheckoutValue(option.dataset.province) || cleanCheckoutValue(option.dataset.province).toLowerCase() === current.province.toLowerCase());
    const selectedNameUnchanged = hasSelectedAddress
      && cleanCheckoutValue(option.dataset.name).toLowerCase() === current.name.toLowerCase();
    const parsedName = storefront.splitFullName(current.name);
    const savedCity = selectedAddressUnchanged ? cleanCheckoutValue(option.dataset.city) : '';
    const locale = window.Shopify?.locale || document.documentElement.lang || 'en';
    const deliveryCity = window.RwadyCheckoutAddresses?.deliveryCity
      ? window.RwadyCheckoutAddresses.deliveryCity(savedCity, current.province, locale)
      : savedCity || current.province;
    return {
      email: current.email,
      phone: current.phone,
      contactPhone,
      deliveryPhone: contactPhone || current.phone,
      address: {
        firstName: selectedNameUnchanged ? cleanCheckoutValue(option.dataset.firstName) || parsedName.firstName : parsedName.firstName,
        lastName: selectedNameUnchanged ? cleanCheckoutValue(option.dataset.lastName) || parsedName.lastName : parsedName.lastName,
        address1: current.address,
        address2: '',
        city: deliveryCity
      }
    };
  };
  const normalizedPhone = (value) => window.RwadyAccountApi?.normalizePhone(value) || '';
  const transferReceiptInput = (container) => container?.querySelector('[data-rwady-transfer-receipt]');
  const transferReceiptOutput = (container) => container?.querySelector('[data-rwady-transfer-receipt-name]');
  const resetTransferReceiptSelection = (container) => {
    const input = transferReceiptInput(container);
    const output = transferReceiptOutput(container);
    if (input) {
      input.value = '';
      input.removeAttribute('aria-invalid');
      input.closest('.rwady-precheckout__receipt-upload')?.classList.remove('rwady-field-invalid');
    }
    otpState.receiptNeedsUpload = false;
    if (output) {
      output.classList.remove('is-error', 'is-selected');
      output.textContent = container?.dir === 'rtl' ? 'صورة JPG أو PNG أو PDF — بحد أقصى 5MB' : 'JPG, PNG or PDF — maximum 5MB';
    }
  };
  const validTransferReceipt = (file) => {
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    return Boolean(file && allowed.includes(String(file.type || '').toLowerCase()) && file.size > 0 && file.size <= 5 * 1024 * 1024);
  };
  const persistTransferReceipt = async (container, authorization, phone) => {
    const file = transferReceiptInput(container)?.files?.[0];
    if (!validTransferReceipt(file)) throw checkoutFailure('transferReceipt', file ? 'INVALID_TRANSFER_RECEIPT' : 'TRANSFER_RECEIPT_REQUIRED');
    if (otpState.receiptUploading) throw checkoutFailure('transferReceipt', 'TRANSFER_RECEIPT_UPLOAD_IN_PROGRESS');
    if (otpState.receiptStoredForAuthorization === authorization.pre_checkout_id && !otpState.receiptNeedsUpload) return { ok: true, status: 'stored' };
    otpState.receiptUploading = true;
    try {
      otpState.receiptUploadPromise = accountClient().uploadTransferReceipt(file, authorization, phone);
      const result = await otpState.receiptUploadPromise;
      if (result?.ok !== true || result.status !== 'stored') throw checkoutFailure('transferReceipt', 'TRANSFER_RECEIPT_NOT_STORED');
      otpState.receiptStoredForAuthorization = authorization.pre_checkout_id;
      otpState.receiptNeedsUpload = false;
      return result;
    } catch (error) {
      if (error?.diagnostics?.stage === 'transferReceipt') throw error;
      throw checkoutFailure('transferReceipt', safeFailureCode(error?.code, 'TRANSFER_RECEIPT_UPLOAD_FAILED'), { status: Number(error?.status) || 0 });
    } finally {
      otpState.receiptUploading = false;
      otpState.receiptUploadPromise = null;
    }
  };
  const unlinkTransferReceipt = async (authorization, phone) => {
    if (!authorization?.pre_checkout_id) return;
    if (otpState.receiptUploadPromise) {
      try { await otpState.receiptUploadPromise; } catch (error) { /* Failed uploads leave nothing to unlink. */ }
    }
    if (otpState.receiptStoredForAuthorization !== authorization.pre_checkout_id) return;
    try {
      const result = await accountClient().unlinkTransferReceipt(authorization, phone);
      if (result?.ok !== true) throw checkoutFailure('transferReceipt', 'TRANSFER_RECEIPT_NOT_UNLINKED');
      otpState.receiptStoredForAuthorization = '';
    } catch (error) {
      if (error?.diagnostics?.stage === 'transferReceipt') throw error;
      throw checkoutFailure('transferReceipt', safeFailureCode(error?.code, 'TRANSFER_RECEIPT_UNLINK_FAILED'), { status: Number(error?.status) || 0 });
    }
  };
  const comparableAddress = (value) => cleanCheckoutValue(value).replace(/(?:,\s*)+$/, '').toLowerCase();
  const savedAddressValue = (option) => [option?.dataset?.address1, option?.dataset?.address2]
    .map(cleanCheckoutValue).filter(Boolean).join(', ');
  const selectedAddressOption = (container) => {
    const select = container.querySelector('[data-rwady-saved-address]');
    const option = select?.options?.[select.selectedIndex];
    return option?.value ? option : null;
  };
  const checkoutAddressPayload = (container, existingAddress, contactPhone = '') => {
    const customer = storefrontCustomer(container, contactPhone);
    const value = (name) => cleanCheckoutValue(container.querySelector(`[data-rwady-field="${name}"]`)?.value);
    return {
      label: cleanCheckoutValue(existingAddress?.label) || (document.documentElement.dir === 'rtl' ? 'المنزل' : 'Home'),
      first_name: customer.address.firstName,
      last_name: customer.address.lastName,
      phone: normalizedPhone(customer.deliveryPhone),
      governorate: value('province'),
      city: customer.address.city,
      area: '',
      address1: value('address'),
      address2: '',
      landmark: '',
      countryCode: 'IQ'
    };
  };
  const addressIdempotencyKey = (authorization) => {
    const id = String(authorization?.pre_checkout_id || '').replace(/[^A-Za-z0-9_-]/g, '');
    if (!id) throw checkoutFailure('addressSave', 'PRE_CHECKOUT_ID_REQUIRED');
    return `precheckout-registration-address-${id}`.slice(0, 128);
  };
  const persistAlternatePhone = async (container, primaryPhone) => {
    const field = container.querySelector('[data-rwady-alternate-phone]');
    const contactPhone = checkoutAlternatePhone(container, primaryPhone);
    if (!contactPhone) return '';
    if (normalizedPhone(field?.dataset.initialPhone) === contactPhone) return contactPhone;
    try {
      const response = await accountClient().saveAlternatePhone(contactPhone);
      if (response?.duplicate_primary === true) return '';
      if (normalizedPhone(response?.alternate_phone) !== contactPhone) throw checkoutFailure('contactPhone', 'CONTACT_PHONE_NOT_PERSISTED');
      if (field) field.dataset.initialPhone = contactPhone;
      return contactPhone;
    } catch (error) {
      if (error?.diagnostics?.stage === 'contactPhone') throw error;
      throw checkoutFailure('contactPhone', safeFailureCode(error?.code, 'CONTACT_PHONE_SAVE_FAILED'), { status: Number(error?.status) || 0 });
    }
  };
  const refreshAddressesAfterAuthorization = async (container, authorization, selectDefault = false) => {
    const addresses = window.RwadyCheckoutAddresses;
    if (!addresses?.refreshAfterAuthorization || !addresses?.useOrderOnlyAfterAuthorization) throw checkoutFailure('addressRefresh', 'ADDRESS_REFRESH_UNAVAILABLE');
    try {
      if (authorization?.account_access !== true) {
        const result = addresses.useOrderOnlyAfterAuthorization(container);
        if (result?.selectionRequired) throw checkoutFailure('addressRefresh', 'ACCOUNT_SESSION_CHANGED');
        return result;
      }
      const result = await addresses.refreshAfterAuthorization(container, { client: accountClient(), selectDefault });
      if (result?.selectionRequired) throw checkoutFailure('addressRefresh', 'ACCOUNT_SESSION_CHANGED');
      return result;
    } catch (error) {
      if (error?.diagnostics?.stage === 'addressRefresh') throw error;
      throw checkoutFailure('addressRefresh', safeFailureCode(error?.code, 'ADDRESS_REFRESH_FAILED'), { status: Number(error?.status) || 0 });
    }
  };
  const persistInitialRegistrationAddress = async (container, authorization, contactPhone = '') => {
    if (authorization?.account_access !== true || authorization?.registration_address_required !== true) {
      return { ok: true, skipped: true };
    }
    try {
      const result = await accountClient().createAddress(
        checkoutAddressPayload(container, null, contactPhone),
        addressIdempotencyKey(authorization)
      );
      if (result?.ok !== true) throw checkoutFailure('addressSave', safeFailureCode(result?.code, 'INITIAL_ADDRESS_NOT_SAVED'));
      authorization.registration_address_required = false;
      return result;
    } catch (error) {
      if (error?.diagnostics?.stage === 'addressSave') throw error;
      throw checkoutFailure('addressSave', safeFailureCode(error?.code, 'ADDRESS_SAVE_FAILED'), { status: Number(error?.status) || 0 });
    }
  };
  const loginElements = () => ({
    modal: page()?.querySelector('[data-rwady-login-modal]'),
    backdrop: page()?.querySelector('[data-rwady-login-backdrop]'),
    phone: page()?.querySelector('[data-rwady-login-phone]'),
    status: page()?.querySelector('[data-rwady-login-status]'),
    send: page()?.querySelector('[data-rwady-login-action="send"]')
  });
  const setLoginStatus = (text, success = false) => {
    const status = loginElements().status;
    if (!status) return;
    status.hidden = !text;
    status.textContent = text || '';
    status.classList.toggle('is-success', success);
  };
  const closeLoginModal = () => {
    const elements = loginElements();
    if (elements.modal) { elements.modal.hidden = true; elements.modal.setAttribute('aria-hidden', 'true'); }
    if (elements.backdrop) elements.backdrop.hidden = true;
    document.body.classList.remove('rwady-otp-open');
  };
  const openLoginModal = () => {
    const elements = loginElements();
    if (!elements.modal || !elements.backdrop || !elements.phone) return;
    elements.phone.value = '';
    setLoginStatus('');
    elements.modal.hidden = false;
    elements.modal.setAttribute('aria-hidden', 'false');
    elements.backdrop.hidden = false;
    document.body.classList.add('rwady-otp-open');
    window.setTimeout(() => elements.phone.focus(), 0);
  };
  const finishCheckoutLogin = async (authorization, phone) => {
    const container = page();
    if (!container || authorization?.account_access !== true) throw checkoutFailure('login', 'ACCOUNT_LOGIN_REQUIRED');
    await refreshAddressesAfterAuthorization(container, authorization, true);
    const phoneField = container.querySelector('[data-rwady-field="phone"]');
    if (phoneField && !cleanCheckoutValue(phoneField.value)) {
      phoneField.value = phone;
      phoneField.dispatchEvent(new Event('input', { bubbles: true }));
    }
    document.dispatchEvent(new CustomEvent('rwady:checkout-login'));
  };
  const startCheckoutLogin = async () => {
    const elements = loginElements();
    if (!elements.phone || otpState.sending || otpState.verifying || otpState.resolve) return;
    const phone = normalizedPhone(elements.phone.value);
    if (!phone) {
      setLoginStatus(document.documentElement.dir === 'rtl' ? 'أدخل رقم هاتف عراقي صحيح.' : 'Enter a valid Iraqi phone number.');
      return;
    }
    otpState.sending = true;
    if (elements.send) elements.send.disabled = true;
    setLoginStatus('');
    try {
      const sent = await accountClient().sendOtp({ phone, intent: 'login', mode: 'login' });
      if (!sent?.challenge_id) throw checkoutFailure('login', safeFailureCode(sent?.code, 'LOGIN_OTP_NOT_SENT'));
      otpState.challengeId = String(sent.challenge_id);
      otpState.phone = phone;
      otpState.mode = 'login';
      otpState.resolve = (authorization) => finishCheckoutLogin(authorization, phone).catch(() => {
        showCheckoutFailure(page()?.querySelector('[data-rwady-error]'), checkoutFailure('login', 'ACCOUNT_LOGIN_REQUIRED'));
      });
      otpState.reject = () => {};
      closeLoginModal();
      openOtpModal(phone);
    } catch (error) {
      if (error?.code === 'ACCOUNT_NOT_FOUND' || error?.status === 404) {
        setLoginStatus(document.documentElement.dir === 'rtl'
          ? 'ليس لديك حساب مسجل بهذا الرقم.'
          : 'You do not have an account registered with this number.');
      } else {
        setLoginStatus(document.documentElement.dir === 'rtl'
          ? 'تعذر تسجيل الدخول الآن. حاول مرة أخرى.'
          : 'Unable to sign in right now. Please try again.');
      }
    } finally {
      otpState.sending = false;
      if (elements.send) elements.send.disabled = false;
    }
  };

  const otpElements = () => ({
    modal: page()?.querySelector('[data-rwady-otp-modal]'),
    backdrop: page()?.querySelector('[data-rwady-otp-backdrop]'),
    phone: page()?.querySelector('[data-rwady-otp-phone]'),
    code: page()?.querySelector('[data-rwady-otp-code]'),
    status: page()?.querySelector('[data-rwady-otp-status]'),
    verify: page()?.querySelector('[data-rwady-otp-action="verify"]'),
    resend: page()?.querySelector('[data-rwady-otp-action="resend"]')
  });
  const setOtpStatus = (text, success = false) => {
    const status = otpElements().status;
    if (!status) return;
    status.hidden = !text;
    status.textContent = text || '';
    status.classList.toggle('is-success', success);
  };
  const closeOtpModal = () => {
    const elements = otpElements();
    if (elements.modal) { elements.modal.hidden = true; elements.modal.setAttribute('aria-hidden', 'true'); }
    if (elements.backdrop) elements.backdrop.hidden = true;
    document.body.classList.remove('rwady-otp-open');
    if (otpState.resendTimer) window.clearTimeout(otpState.resendTimer);
    otpState.resendTimer = 0;
  };
  const openOtpModal = (phone) => {
    const elements = otpElements();
    if (!elements.modal || !elements.backdrop || !elements.code) throw checkoutFailure('otpSend', 'OTP_UI_UNAVAILABLE');
    elements.phone.textContent = phone;
    elements.code.value = '';
    elements.modal.hidden = false;
    elements.modal.setAttribute('aria-hidden', 'false');
    elements.backdrop.hidden = false;
    document.body.classList.add('rwady-otp-open');
    setOtpStatus('');
    elements.code.focus();
  };
  const authorizeCheckoutPhone = async (phone, fullName) => {
    return accountClient().authorizeCheckout({ phone, full_name: fullName });
  };
  const authorizedCheckoutPayload = (payload) => payload?.authorized === true && payload.pre_checkout_id && payload.verification_token;
  const requestOtpAuthorization = async (phone, fullName) => {
    if (otpState.authorization && normalizedPhone(phone) === otpState.authorizationPhone) return otpState.authorization;
    const authorization = await authorizeCheckoutPhone(phone, fullName);
    if (authorizedCheckoutPayload(authorization)) {
      otpState.authorization = authorization;
      otpState.authorizationPhone = normalizedPhone(phone);
      return authorization;
    }
    if (authorization?.code !== 'OTP_REQUIRED') {
      throw checkoutFailure('authorize', safeFailureCode(authorization?.code, 'CHECKOUT_AUTHORIZATION_FAILED'));
    }
    if (otpState.sending || otpState.verifying || otpState.resolve) throw checkoutFailure('otpSend', 'OTP_FLOW_IN_PROGRESS');
    otpState.mode = 'checkout';
    otpState.sending = true;
    try {
      const sent = await accountClient().sendOtp({ phone, full_name: fullName });
      if (!sent.challenge_id) throw checkoutFailure('otpSend', sent.code || 'OTP_SEND_REJECTED');
      otpState.challengeId = String(sent.challenge_id);
      otpState.phone = phone;
      openOtpModal(phone);
      otpState.sending = false;
      return await new Promise((resolve, reject) => { otpState.resolve = resolve; otpState.reject = reject; });
    } catch (error) {
      otpState.sending = false;
      throw error;
    }
  };
  const verifyOtpAuthorization = async () => {
    if (otpState.verifying || !otpState.resolve) return;
    const elements = otpElements();
    const code = window.RwadyAccountApi.normalizeDigits(elements.code?.value || '').replace(/\D/g, '').slice(0, 6);
    if (!/^\d{6}$/.test(code)) { setOtpStatus(document.documentElement.dir === 'rtl' ? 'أدخل رمز التحقق المكوّن من 6 أرقام.' : 'Enter the 6-digit verification code.'); return; }
    otpState.verifying = true;
    if (elements.verify) elements.verify.disabled = true;
    try {
      const currentPhone = cleanCheckoutValue(page()?.querySelector('[data-rwady-field="phone"]')?.value);
      if (otpState.mode !== 'login' && window.RwadyAccountApi.normalizePhone(currentPhone) !== window.RwadyAccountApi.normalizePhone(otpState.phone)) {
        throw checkoutFailure('otpVerify', 'PHONE_CHANGED_AFTER_VERIFICATION');
      }
      const verification = await accountClient().verifyOtp({ challenge_id: otpState.challengeId, phone: otpState.phone, code });
      const authorization = await authorizeCheckoutPhone(otpState.phone);
      if (!authorizedCheckoutPayload(authorization)) throw checkoutFailure('authorize', safeFailureCode(authorization?.code, 'OTP_SESSION_NOT_AUTHORIZED'));
      authorization.account_access = authorization.account_access === true;
      authorization.registration_address_required = authorization.registration_address_required === true || verification?.registration_address_required === true;
      otpState.authorization = authorization;
      otpState.authorizationPhone = normalizedPhone(otpState.phone);
      const resolve = otpState.resolve;
      otpState.resolve = null;
      otpState.reject = null;
      closeOtpModal();
      resolve(authorization);
    } catch (error) {
      setOtpStatus(error?.code === 'INVALID_OTP'
        ? (document.documentElement.dir === 'rtl' ? 'رمز التحقق غير صحيح.' : 'The verification code is incorrect.')
        : (document.documentElement.dir === 'rtl' ? 'تعذر التحقق من الرمز. حاول مرة أخرى.' : 'Unable to verify the code. Please try again.'));
    } finally {
      otpState.verifying = false;
      if (elements.verify) elements.verify.disabled = false;
    }
  };
  const nativeBasePayload = (cart, customer, authorization, container) => {
    const shipping = shippingAmountIqd(container);
    const city = cleanCheckoutValue(customer.address.city);
    return {
      pre_checkout_id: authorization.pre_checkout_id,
      verification_token: authorization.verification_token,
      customer: {
        name: cleanCheckoutValue(container.querySelector('[data-rwady-field="name"]')?.value),
        phone: normalizedPhone(container.querySelector('[data-rwady-field="phone"]')?.value),
        phone2: customer.contactPhone || undefined,
        address: cleanCheckoutValue(container.querySelector('[data-rwady-field="address"]')?.value),
        notes: cleanCheckoutValue(container.querySelector('[data-rwady-field="notes"]')?.value)
      },
      items: (cart.items || []).map((item) => ({
        name: cleanCheckoutValue(item.product_title || item.title),
        sku: cleanCheckoutValue(item.sku),
        quantity: Math.max(1, Number(item.quantity) || 1),
        unit_price: Math.max(0, Math.round(Number(item.final_price ?? item.price ?? 0) / 100)),
        shopify_variant_id: cleanCheckoutValue(item.variant_id)
      })),
      shipping,
      customer_shipping: shipping,
      payment_method: selectedPaymentMethod(container),
      email: customer.email || undefined,
      governorate: cleanCheckoutValue(container.querySelector('[data-rwady-field="province"]')?.value),
      city,
      metadata: { source: 'shopify_pre_checkout', currency: 'IQD', city }
    };
  };
  let orderClient;
  let activeReview = null;
  let reviewBusy = false;
  let reviewRevision = 0;
  let restoreTimer;
  const nativeClient = () => {
    if (!orderClient) {
      try { orderClient = window.RwadyOrderConfirmation.create({ baseUrl: page().dataset.accountApiBase, storage: window.localStorage }); }
      catch { throw checkoutFailure('order', 'CHECKOUT_STORAGE_UNAVAILABLE'); }
    }
    return orderClient;
  };
  const nativeText = (ar, en) => document.documentElement.dir === 'rtl' ? ar : en;
  const nativePayload = (cart, customer, authorization, container, loyalty) => {
    const payload = nativeBasePayload(cart, customer, authorization, container);
    const method = selectedPaymentMethod(container);
    const codes = (cart.discount_codes || []).map((entry) => typeof entry === 'string' ? entry : entry.code);
    (cart.cart_level_discount_applications || []).filter((entry) => entry.type === 'discount_code').forEach((entry) => codes.push(entry.title));
    if (loyalty?.code) codes.push(loyalty.code);
    const attributes = { ...(cart.attributes || {}) };
    appendStandardPaymentAttributes(container, attributes);
    appendPaymentPricingAttributes(container, attributes, loyalty?.value_iqd || 0);
    payload.items.forEach((item, index) => {
      const imageUrl = cart.items[index]?.featured_image?.url;
      const receiptCard = [...container.querySelectorAll('[data-cart-item-key]')].find((card) => card.dataset.receiptVariant === String(cart.items[index]?.variant_id));
      item.image_url = undefined;
      if (imageUrl) {
        try {
          const resolvedImage = new URL(imageUrl, window.location.origin);
          if (resolvedImage.protocol === 'https:') item.image_url = resolvedImage.href;
        } catch { /* A missing image must not block the delivery review. */ }
      }
      item.warranty = cleanCheckoutValue(receiptCard?.dataset.receiptWarranty);
      item.properties = Object.fromEntries(Object.entries(cart.items[index]?.properties || {}).map(([key, value]) => [key, String(value ?? '')]));
      item.selling_plan_id = cart.items[index]?.selling_plan_allocation?.selling_plan?.id || undefined;
    });
    const displayedBase = container.querySelector('[data-rwady-cart-shipping]')?.dataset.cartTotalCents;
    const displayedCents = displayedBase === undefined || displayedBase === '' ? NaN : Number(displayedBase);
    if (!Number.isFinite(displayedCents) || displayedCents < 0) throw checkoutFailure('pricing', 'PRICING_UNAVAILABLE');
    const productsTotal = Math.max(0, Math.round(displayedCents / 100) - (loyalty?.value_iqd || 0));
    const payableProducts = method === 'installment' ? Number(attributes.pre_checkout_installment_total_iqd)
      : productsTotal;
    const expectedOrderTotal = method === 'online'
      ? Number(attributes.pre_checkout_online_payable_amount_iqd)
      : payableProducts + payload.shipping;
    return {
      ...payload, attributes, discount_codes: [...new Set(codes.filter(Boolean))],
      expected_products_total: productsTotal,
      expected_order_total: expectedOrderTotal,
      payment_detail: container.querySelector(`[data-payment-panel="${method}"] [data-rwady-field="payment_detail"]`)?.value || '',
      country_code: container.querySelector('[data-rwady-cart-shipping]')?.dataset.countryCode || 'IQ',
      currency: cart.currency || 'IQD'
    };
  };
  const renderReview = (review) => {
    const container = page();
    if (!container) return;
    activeReview = review;
    const output = container.querySelector('[data-rwady-review-status]');
    const refresh = container.querySelector('[data-rwady-review-refresh]');
    const request = container.querySelector('[data-rwady-review-request]');
    const notice = container.querySelector('[data-rwady-review-notice]');
    const noticeTitle = container.querySelector('[data-rwady-review-notice-title]');
    const noticeText = container.querySelector('[data-rwady-review-notice-text]');
    const whatsapp = container.querySelector('[data-rwady-review-whatsapp]');
    const approved = review?.status === 'approved';
    const rejected = review?.status === 'rejected';
    if (output) {
      output.hidden = !review;
      const messages = {
        pending: nativeText('تم إرسال طلب مراجعة سعر التوصيل. سيتم تحديث النتيجة تلقائيًا بدون تحديث الصفحة.', 'Your delivery price review request was sent. The result will update automatically without refreshing the page.'),
        approved: nativeText('تم تعديل كلفة التوصيل إلى ', 'Delivery cost updated to ') + formatIqdFromCents(Number(review?.shipping) * 100),
        rejected: nativeText('تم رفض تعديل كلفة التوصيل وتم اعتماد الأجرة الأصلية.', 'The delivery cost change was declined and the original delivery cost is being used.'),
        stale: nativeText('تغيّرت السلة أو معلومات التوصيل. اطلب مراجعة جديدة.', 'The cart or delivery details changed. Request a new review.')
      };
      output.textContent = messages[review?.status] || '';
    }
    if (notice) {
      notice.hidden = !review;
      notice.dataset.reviewStatus = review?.status || '';
      if (noticeTitle) {
        noticeTitle.textContent = review?.status === 'pending'
          ? nativeText('تم إرسال طلب مراجعة سعر التوصيل', 'Delivery price review request sent')
          : approved
            ? nativeText('تم تعديل كلفة التوصيل', 'Delivery cost updated')
            : rejected
              ? nativeText('تم رفض تعديل كلفة التوصيل', 'Delivery cost change declined')
              : nativeText('تحتاج المراجعة إلى إعادة إرسال', 'Review needs to be requested again');
      }
      if (noticeText) {
        noticeText.textContent = review?.status === 'pending'
          ? nativeText('لا تحتاج لتحديث الصفحة. سنعرض النتيجة هنا تلقائيًا عند رد فريق المبيعات.', 'You do not need to refresh the page. The result will appear here automatically when the sales team responds.')
          : approved
            ? nativeText('الكلفة الجديدة: ', 'New delivery cost: ') + formatIqdFromCents(Number(review?.shipping) * 100)
            : rejected
              ? nativeText('تم الإبقاء على أجرة التوصيل الأصلية ويمكنك إكمال الطلب.', 'The original delivery cost remains in place and you can continue your order.')
              : nativeText('تغيّرت بيانات الطلب؛ اطلب مراجعة جديدة.', 'Order details changed; request a new review.');
      }
    }
    if (whatsapp) {
      whatsapp.hidden = review?.status !== 'pending';
      const phone = normalizedPhone(container.querySelector('[data-rwady-field="phone"]')?.value);
      const text = nativeText(
        'السلام عليكم، لدي طلب مراجعة أجرة توصيل ولم تظهر النتيجة بعد.' + (phone ? ' رقم الهاتف: ' + phone : ''),
        'Hello, I requested a delivery cost review and have not received the result yet.' + (phone ? ' Phone: ' + phone : '')
      );
      whatsapp.href = 'https://wa.me/9647809655444?text=' + encodeURIComponent(text);
    }
    if (refresh) refresh.hidden = !review || review.status === 'stale';
    if (request) request.hidden = false;
    window.RwadyCartShipping?.setReviewedPrice?.(container.querySelector('[data-rwady-cart-shipping]'), approved ? review.shipping : null);
    restorePaymentPresentation();
  };
  const checkoutPhoneError = (value) => {
    if (normalizedPhone(value)) return '';
    let digits = String(value || '').replace(/[٠-٩۰-۹]/g, digit => String('٠١٢٣٤٥٦٧٨٩'.indexOf(digit) >= 0 ? '٠١٢٣٤٥٦٧٨٩'.indexOf(digit) : '۰۱۲۳۴۵۶۷۸۹'.indexOf(digit))).replace(/\D/g, '');
    if (digits.startsWith('00964')) digits = '0' + digits.slice(5);
    else if (digits.startsWith('964')) digits = '0' + digits.slice(3);
    if (digits && !digits.startsWith('0')) return nativeText('يجب أن يبدأ رقم الهاتف بـ0.', 'The phone number must start with 0.');
    if (digits.length !== 11) return nativeText('يجب أن يكون رقم الهاتف 11 رقمًا.', 'The phone number must contain 11 digits.');
    return nativeText('أدخل رقم هاتف عراقي صحيح يبدأ بـ07.', 'Enter a valid Iraqi mobile number starting with 07.');
  };
  const showCheckoutPhoneError = (field, reveal = true) => {
    const message = reveal ? checkoutPhoneError(field.value) : '';
    const output = field.closest('.rwady-precheckout__field')?.querySelector('[data-rwady-phone-error]');
    if (output) {
      output.textContent = message;
      output.hidden = !message;
    }
    if (!message) {
      const summary = page()?.querySelector('[data-rwady-error]');
      const phoneMessages = [
        nativeText('يجب أن يبدأ رقم الهاتف بـ0.', 'The phone number must start with 0.'),
        nativeText('يجب أن يكون رقم الهاتف 11 رقمًا.', 'The phone number must contain 11 digits.'),
        nativeText('أدخل رقم هاتف عراقي صحيح يبدأ بـ07.', 'Enter a valid Iraqi mobile number starting with 07.')
      ];
      if (summary && phoneMessages.includes(summary.textContent)) {
        summary.textContent = '';
        summary.hidden = true;
      }
    }
    field.setAttribute('aria-invalid', String(Boolean(message)));
    field.closest('.rwady-precheckout__field')?.classList.toggle('rwady-field-invalid', Boolean(message));
    return message;
  };
  const validCheckoutField = (field) => field.checkValidity()
    && (!field.matches('[data-rwady-field="phone"]') || Boolean(normalizedPhone(field.value)));
  const markCheckoutFields = (container, includeTerms = false, reveal = true) => {
    const fields = [...container.querySelectorAll('[data-rwady-field]')].filter(field => !field.disabled && (includeTerms || field.type !== 'file'));
    const receipt = container.querySelector('[data-rwady-transfer-receipt]');
    const receiptRequired = cleanCheckoutValue(container.querySelector('[data-rwady-field="payment"]')?.value) === 'electronic_transfer';
    if (receiptRequired && receipt && !receipt.disabled) fields.push(receipt);
    if (includeTerms) fields.push(container.querySelector('[data-rwady-terms]'));
    const invalid = fields.filter(field => field && (field.matches('[data-rwady-terms]') ? !field.checked : field.matches('[data-rwady-transfer-receipt]') ? !validTransferReceipt(field.files?.[0]) : !validCheckoutField(field)));
    if (reveal) {
      fields.filter(Boolean).forEach(field => {
        const bad = invalid.includes(field);
        if (field.matches('[data-rwady-field="phone"]')) showCheckoutPhoneError(field, bad);
        field.setAttribute('aria-invalid', String(bad));
        field.closest('.rwady-precheckout__field, .rwady-precheckout__terms, .rwady-precheckout__receipt-upload')?.classList.toggle('rwady-field-invalid', bad);
      });
      if (invalid.length) {
        const first = invalid[0];
        const wrapper = first.closest('.rwady-precheckout__field, .rwady-precheckout__terms, .rwady-precheckout__receipt-upload');
        (wrapper || first).scrollIntoView({ behavior: 'smooth', block: 'center' });
        const target = first.matches('[data-rwady-transfer-receipt]') ? first : first.getClientRects().length ? first : wrapper?.querySelector('button');
        target?.focus({ preventScroll: true });
      }
    }
    return invalid.length === 0;
  };
  const goToThankYou = () => {
    const url = new URL(window.location.href);
    url.searchParams.set('view', 'rwady-thank-you');
    url.hash = '';
    window.location.replace(url.href);
  };
  const goToOrderConfirmation = () => {
    window.location.assign(`${root()}pages/order-confirmation`);
  };
  let orderReviewTransitionTimer = 0;
  const stopOrderReviewTransition = (container) => {
    if (orderReviewTransitionTimer) window.clearInterval(orderReviewTransitionTimer);
    orderReviewTransitionTimer = 0;
    const status = container?.querySelector('[data-rwady-transition]');
    const button = container?.querySelector('[data-rwady-continue]');
    if (status) status.hidden = true;
    if (button) button.removeAttribute('aria-busy');
  };
  const startOrderReviewTransition = (container) => {
    stopOrderReviewTransition(container);
    const status = container?.querySelector('[data-rwady-transition]');
    const output = status?.querySelector('[data-rwady-transition-text]');
    const button = container?.querySelector('[data-rwady-continue]');
    if (!status || !output) return;
    const startedAt = Date.now();
    const render = () => {
      const seconds = Math.max(0, Math.floor((Date.now() - startedAt) / 1000));
      output.textContent = nativeText(`جاري إرسالك إلى صفحة تأكيد الطلب… ${seconds} ثانية`, `Taking you to the order confirmation page… ${seconds} sec`);
    };
    render();
    status.hidden = false;
    if (button) button.setAttribute('aria-busy', 'true');
    orderReviewTransitionTimer = window.setInterval(render, 1000);
  };
  const trackAddPaymentInfo = async (result, payload) => {
    if (!result?.request_id || result?.status !== 'prepared') return false;
    const eventId = `rwady_add_payment_info_${result.request_id}`;
    const storageKey = `rwady-meta-${eventId}`;
    try { if (sessionStorage.getItem(storageKey) === 'sent') return false; } catch (_) {}
    let pixelReady = Promise.resolve();
    if (typeof window.fbq !== 'function') {
      const pixelId = String(page()?.dataset.metaPixelId || '').trim();
      if (!/^\d{8,20}$/.test(pixelId)) return false;
      const fbq = function(){ fbq.callMethod ? fbq.callMethod.apply(fbq, arguments) : fbq.queue.push(arguments); };
      fbq.push = fbq; fbq.loaded = true; fbq.version = '2.0'; fbq.queue = [];
      window.fbq = fbq;
      window.fbq('init', pixelId);
      pixelReady = new Promise((resolve) => {
        const script = document.createElement('script');
        const finish = () => resolve();
        script.async = true;
        script.src = 'https://connect.facebook.net/en_US/fbevents.js';
        script.addEventListener('load', finish, { once: true });
        script.addEventListener('error', finish, { once: true });
        document.head.append(script);
        window.setTimeout(finish, 2000);
      });
    }
    const items = Array.isArray(payload?.items) ? payload.items : [];
    const contents = items.map((item) => ({
      id: String(item.shopify_variant_id || item.sku || ''),
      quantity: Math.max(1, Number(item.quantity) || 1),
      item_price: Math.max(0, Number(item.unit_price) || 0)
    })).filter((item) => item.id);
    const total = Number(payload.expected_products_total);
    const parameters = {
      content_type: 'product',
      content_ids: contents.map((item) => item.id),
      contents,
      num_items: contents.reduce((sum, item) => sum + item.quantity, 0),
      payment_method: String(payload?.payment_method || ''),
      currency: 'IQD',
      value: Number.isFinite(total) && total >= 0 ? total : 0
    };
    window.fbq('track', 'AddPaymentInfo', parameters, { eventID: eventId });
    await pixelReady;
    await new Promise((resolve) => window.setTimeout(resolve, 450));
    try { sessionStorage.setItem(storageKey, 'sent'); } catch (_) {}
    return true;
  };
  const checkoutFormSnapshot = (container) => {
    const value = (selector) => container?.querySelector(selector)?.value || '';
    const payment = value('[data-rwady-field="payment"]');
    return {
      fields: {
        name: value('[data-rwady-field="name"]'),
        phone: value('[data-rwady-field="phone"]'),
        email: value('[data-rwady-field="email"]'),
        province: value('[data-rwady-field="province"]'),
        address: value('[data-rwady-field="address"]'),
        notes: value('[data-rwady-field="notes"]'),
        payment,
        payment_detail: value(`[data-payment-panel="${payment}"] [data-rwady-field="payment_detail"]`)
      },
      alternate_phone: value('[data-rwady-alternate-phone]'),
      location: container?.querySelector('[data-rwady-location]')?.dataset.coordinates || ''
    };
  };
  const saveOrderConfirmationDraft = (result, payload, authorization, cart) => {
    const record = nativeClient().peek();
    if (!window.RwadyOrderConfirmation.validPrepared(result, record?.request_id)) throw checkoutFailure('prepare', 'ORDER_CONFIRMATION_UNAVAILABLE');
    const items = (payload.items || []).map((item, index) => ({
      ...item,
      image_url: item.image_url || cart.items?.[index]?.featured_image?.url || '',
      warranty: item.warranty || ''
    }));
    sessionStorage.setItem('rwady-order-confirmation-draft-v1', JSON.stringify({
      version: 1,
      request_id: result.request_id,
      prepared_at: result.confirmation.prepared_at || new Date().toISOString(),
      expires_at: result.confirmation.expires_at || '',
      authorization: {
        pre_checkout_id: authorization.pre_checkout_id,
        verification_token: authorization.verification_token,
        account_access: authorization.account_access === true
      },
      form: checkoutFormSnapshot(page()),
      payload: { ...payload, items },
      confirmation: { ...result.confirmation, items }
    }));
  };
  const showConfirmedOrder = (result, method, payload, confirmedCart) => {
    const container = page();
    if (!container || !window.RwadyOrderConfirmation.validOrder(result, nativeClient().peek()?.request_id)) return;
    container.dataset.orderSubmitted = 'true';
    try {
      const items = [...container.querySelectorAll('[data-cart-item-key]')].map(item => {
        const line = confirmedCart?.items?.find(line => line.key === item.dataset.cartItemKey);
        return {
        title: item.querySelector('.rwady-precheckout__item-title')?.textContent.trim() || '',
        detail: item.querySelector('.rwady-precheckout__item-total')?.textContent.trim() || '',
        image: item.querySelector('img')?.currentSrc || item.querySelector('img')?.src || '',
        url: item.querySelector('.rwady-precheckout__item-title')?.href || '',
        sku: item.dataset.receiptSku || '',
        warranty: item.dataset.receiptWarranty || '',
        quantity: line?.quantity ?? Number(item.dataset.receiptQuantity),
        unit_price: line ? Number(line.final_price ?? line.price) / 100 : Number(item.dataset.receiptPrice) / 100
      };
      });
      if (items.length) sessionStorage.setItem('rwady-thank-you-summary', JSON.stringify({
        request_id: result.request_id, items, shipping: payload?.shipping ?? shippingAmountIqd(container),
        products_total: Number(payload?.expected_products_total),
        customer: payload?.customer ? {name: payload.customer.name, phone: payload.customer.phone, address: payload.customer.address} : undefined,
        created_at: new Date().toISOString(),
        warranty_notes: [...container.querySelectorAll('.rwady-precheckout__notice:first-of-type ol li')].map(item => item.textContent.trim())
      }));
    } catch (_) {}
    container.querySelectorAll('[data-rwady-field], [data-rwady-quantity], [data-rwady-remove-item], [data-rwady-terms], [data-rwady-continue]').forEach((field) => { field.disabled = true; });
    const errorOutput = container.querySelector('[data-rwady-error]');
    if (errorOutput) errorOutput.hidden = true;
  };
  const reviewAction = async (action, passive = false) => {
    const container = page();
    if (!container || reviewBusy || otpState.checkoutStarted || container.dataset.orderSubmitted) return;
    if (!markCheckoutFields(container, false, !passive)) {
      if (!passive) {
        const output = container.querySelector('[data-rwady-review-error]');
        if (output) output.hidden = false;
      }
      return;
    }
    const reviewError = container.querySelector('[data-rwady-review-error]');
    if (reviewError) reviewError.hidden = true;
    reviewBusy = true;
    const revision = reviewRevision;
    container.querySelectorAll('[data-rwady-review-request], [data-rwady-review-refresh]').forEach((button) => { button.disabled = true; });
    try {
      const phone = normalizedPhone(container.querySelector('[data-rwady-field="phone"]')?.value);
      const authorization = passive
        ? await authorizeCheckoutPhone(phone)
        : await requestOtpAuthorization(phone, cleanCheckoutValue(container.querySelector('[data-rwady-field="name"]')?.value));
      if (!authorizedCheckoutPayload(authorization)) return;
      const response = await fetch(`${root()}cart.js`, { cache: 'no-store' });
      if (!response.ok) throw checkoutFailure('review', 'CART_READ_FAILED');
      const cart = await response.json();
      if (normalizedPhone(container.querySelector('[data-rwady-field="phone"]')?.value) !== phone) throw checkoutFailure('review', 'PHONE_CHANGED_AFTER_VERIFICATION');
      const customer = storefrontCustomer(container, checkoutAlternatePhone(container, phone));
      const payload = nativePayload(cart, customer, authorization, container);
      const result = await nativeClient().request(action, payload, authorization);
      if (result.status === 'submitted') { showConfirmedOrder(result, payload.payment_method, payload); goToThankYou(); return; }
      if (!passive) {
        const error = container.querySelector('[data-rwady-error]');
        if (error) error.hidden = true;
      }
      if (revision === reviewRevision) renderReview(result.review);
      else restoreNativeReview();
    } catch (error) {
      if (error?.code === 'INVALID_AUTHORIZATION') otpState.authorization = null;
      if (!passive) showCheckoutFailure(container.querySelector('[data-rwady-error]'), error);
    } finally {
      reviewBusy = false;
      container.querySelectorAll('[data-rwady-review-request], [data-rwady-review-refresh]').forEach((button) => { button.disabled = false; });
    }
  };
  const restoreNativeReview = () => {
    clearTimeout(restoreTimer);
    restoreTimer = setTimeout(() => {
      try {
        const record = nativeClient().peek();
        const payment = page()?.querySelector('[data-rwady-field="payment"]');
        if (record?.review && payment && !payment.value && [...payment.options].some((option) => option.value === record.payment_method)) {
          payment.value = record.payment_method;
          payment.dispatchEvent(new Event('change', { bubbles: true }));
          const detail = page().querySelector(`[data-payment-panel="${payment.value}"] [data-rwady-field="payment_detail"]`);
          if (detail && [...detail.options].some((option) => option.value === record.payment_detail)) {
            detail.value = record.payment_detail;
            detail.dispatchEvent(new Event('change', { bubbles: true }));
          }
          restorePaymentPresentation();
        }
        if (record?.phase === 'submitted' && !page()?.querySelector('[data-cart-item-key]') && window.RwadyOrderConfirmation.validOrder(record.result, record.request_id)) goToThankYou();
        else if (record) reviewAction('review_status', true);
      } catch (_) { /* Explicit confirmation reports unavailable storage. */ }
    }, 900);
  };
  document.addEventListener('click', (event) => {
    const request = event.target.closest('[data-rwady-review-request]');
    const refresh = event.target.closest('[data-rwady-review-refresh]');
    if (request || refresh) {
      event.preventDefault();
      const existingReview = activeReview?.status === 'pending' || activeReview?.status === 'approved';
      reviewAction(request && !existingReview ? 'review_request' : 'review_status');
    }
  });
  const invalidateReview = (event) => {
    if (event?.target && !event.target.matches('[data-rwady-field="phone"], [data-rwady-field="province"], [data-rwady-field="address"], [data-rwady-field="payment"], [data-rwady-field="payment_detail"], [data-rwady-saved-address]')) return;
    reviewRevision += 1;
    if (activeReview) renderReview(null);
    restoreNativeReview();
  };
  const clearFieldError = event => {
    const field = event.target;
    if (!field.matches('[data-rwady-field], [data-rwady-terms]')) return;
    if (field.matches('[data-rwady-field="phone"]')) {
      showCheckoutPhoneError(field, Boolean(field.value.trim()));
    }
    const valid = field.matches('[data-rwady-terms]') ? field.checked : field.matches('[data-rwady-transfer-receipt]') ? validTransferReceipt(field.files?.[0]) : validCheckoutField(field);
    if (valid) {
      field.removeAttribute('aria-invalid');
      field.closest('.rwady-precheckout__field, .rwady-precheckout__terms, .rwady-precheckout__receipt-upload')?.classList.remove('rwady-field-invalid');
    }
  };
  document.addEventListener('input', clearFieldError);
  document.addEventListener('change', clearFieldError);
  document.addEventListener('input', invalidateReview);
  document.addEventListener('change', invalidateReview);
  document.addEventListener('maximize:cart:updated', () => { reviewRevision += 1; renderReview(null); restoreNativeReview(); });
  window.addEventListener('pageshow', restoreNativeReview);
  document.addEventListener('rwady:shipping-updated', restoreNativeReview);
  window.setInterval(() => { if (!document.hidden && activeReview?.status === 'pending') reviewAction('review_status', true); }, 15000);
  restoreNativeReview();

  const initializeMobileBottomNavigation = () => {
    const headerContainer = document.getElementById('x-header-container');
    if (!headerContainer || !page()) return;

    const originalParent = headerContainer.parentNode;
    const originalNextSibling = headerContainer.nextSibling;
    const mobileViewport = window.matchMedia('(max-width: 767px)');
    const visualViewport = window.visualViewport;
    let visualViewportFrame = 0;
    const updateVisualViewportBottom = () => {
      if (visualViewportFrame) cancelAnimationFrame(visualViewportFrame);
      visualViewportFrame = requestAnimationFrame(() => {
        visualViewportFrame = 0;
        const navigation = headerContainer.querySelector('.rwady-mobile-bottom-nav');
        if (!navigation || !mobileViewport.matches) return;
        const visibleBottom = visualViewport
          ? visualViewport.offsetTop + visualViewport.height
          : window.innerHeight;
        navigation.style.setProperty('--rwady-precheckout-visible-bottom', `${Math.round(visibleBottom)}px`);
      });
    };
    const syncPosition = () => {
      if (mobileViewport.matches) {
        if (headerContainer.parentNode !== document.body) document.body.appendChild(headerContainer);
        updateVisualViewportBottom();
        return;
      }

      if (headerContainer.parentNode !== originalParent) {
        originalParent.insertBefore(headerContainer, originalNextSibling?.parentNode === originalParent ? originalNextSibling : null);
      }
    };

    mobileViewport.addEventListener('change', syncPosition);
    visualViewport?.addEventListener('resize', updateVisualViewportBottom, { passive: true });
    visualViewport?.addEventListener('scroll', updateVisualViewportBottom, { passive: true });
    window.addEventListener('orientationchange', updateVisualViewportBottom, { passive: true });
    syncPosition();
  };

  initializeMobileBottomNavigation();

  const formatIqdFromCents = (cents) => {
    const amount = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Math.round(Number(cents) / 100));
    return document.documentElement.dir === 'rtl' ? `د.ع ${amount}` : `${amount} IQD`;
  };

  const updatePaymentTotals = (paymentMethod, paymentDetail = '') => {
    const summary = page()?.querySelector('[data-rwady-cart-shipping]');
    if (!summary) return;
    const loyaltyCents = Math.max(0, Number(page()?.querySelector('rwady-loyalty[data-surface="checkout"]')?.dataset.redemptionIqd || 0) * 100);
    const regularCents = Math.max(0, Number(summary.dataset.cartTotalCents || 0) - loyaltyCents);
    const installmentCents = loyaltyCents ? Math.round(regularCents * 1.21052565) : Number(summary.dataset.installmentTotalCents || regularCents);
    const discountOutput = summary.querySelector('.is-discount dd');
    if (discountOutput) {
      if (!('originalDiscountText' in discountOutput.dataset)) discountOutput.dataset.originalDiscountText = discountOutput.textContent;
      discountOutput.textContent = loyaltyCents ? '−' + formatIqdFromCents(loyaltyCents) : discountOutput.dataset.originalDiscountText;
    }
    const cardRate = paymentMethod === 'online'
      ? Number(paymentDetail === 'local_card' ? summary.dataset.localCardRate : paymentDetail === 'international_card' ? summary.dataset.internationalCardRate : 0)
      : 0;
    const shipping = Number(summary.dataset.shippingPrice || 0);
    const shippingCents = Math.max(0, Math.round(shipping * 100));
    const onlineFeeBaseCents = regularCents + shippingCents;
    const cardFeeCents = Math.round(onlineFeeBaseCents * cardRate);
    const effectiveCents = paymentMethod === 'installment' ? installmentCents : regularCents + cardFeeCents;
    summary.dataset.cartEffectiveTotalCents = String(effectiveCents);
    const productsOutput = summary.querySelector('[data-rwady-products-total]');
    const monthlyOutput = summary.querySelector('[data-rwady-monthly-total]');
    const monthlyRow = summary.querySelector('[data-rwady-monthly-row]');
    const grandTotalRow = summary.querySelector('[data-rwady-grand-total-row]');
    const cardFeeRow = summary.querySelector('[data-rwady-card-fee-row]');
    const cardFeeOutput = summary.querySelector('[data-rwady-card-fee]');
    const cardFeeLabel = summary.querySelector('[data-rwady-card-fee-label]');
    const productsLabel = summary.querySelector('[data-rwady-products-label]');
    const shippingLabel = summary.querySelector('[data-rwady-shipping-label]');
    const orderOutput = summary.querySelector('[data-rwady-order-total]');
    const isInstallment = paymentMethod === 'installment';
    if (productsOutput) productsOutput.textContent = formatIqdFromCents(
      (paymentMethod === 'installment' ? effectiveCents : regularCents) + loyaltyCents
    );
    if (monthlyOutput) monthlyOutput.textContent = formatIqdFromCents(installmentCents / 10);
    if (monthlyRow) monthlyRow.hidden = !isInstallment;
    if (grandTotalRow) grandTotalRow.hidden = isInstallment;
    if (cardFeeRow) cardFeeRow.hidden = cardFeeCents <= 0;
    if (cardFeeOutput) cardFeeOutput.textContent = formatIqdFromCents(cardFeeCents);
    if (cardFeeLabel && cardRate > 0) cardFeeLabel.textContent = `${document.documentElement.dir === 'rtl' ? 'رسوم الدفع الإلكتروني' : 'Online payment fee'} (${cardRate * 100}%)`;
    if (productsLabel) productsLabel.textContent = isInstallment ? productsLabel.dataset.installmentLabel : productsLabel.dataset.defaultLabel;
    if (shippingLabel) shippingLabel.textContent = isInstallment ? shippingLabel.dataset.installmentLabel : shippingLabel.dataset.defaultLabel;
    if (orderOutput) orderOutput.textContent = formatIqdFromCents(
      paymentMethod === 'online'
        ? regularCents + shippingCents + cardFeeCents
        : effectiveCents + shippingCents
    );
  };

  document.addEventListener('rwady:loyalty-changed', () => {
    const container = page();
    updatePaymentTotals(container?.querySelector('[data-rwady-field="payment"]')?.value || '', container?.querySelector('[data-payment-panel="online"] [data-rwady-field="payment_detail"]')?.value || '');
  });

  const updatePaymentTheme = (select) => {
    const selected = select.value || 'default';
    const card = page()?.querySelector('[data-rwady-price-card]');
    const paymentArea = page()?.querySelector('[data-rwady-payment-area]');
    const title = card?.querySelector('[data-rwady-price-title]');
    const optionLabel = select.options[select.selectedIndex]?.textContent.trim();
    if (card) card.dataset.paymentTheme = selected;
    if (paymentArea) paymentArea.dataset.paymentTheme = selected;
    if (title) {
      const fallback = document.documentElement.dir === 'rtl' ? 'ملخص سعر الطلب' : 'Order price summary';
      title.textContent = selected === 'default' ? fallback : optionLabel;
    }
  };

  const updateTransferInformation = (method) => {
    const box = page()?.querySelector('[data-rwady-transfer-info]');
    const primary = box?.querySelector('[data-rwady-transfer-primary]');
    const secondary = box?.querySelector('[data-rwady-transfer-secondary]');
    const priceCard = page()?.querySelector('[data-rwady-price-card]');
    const paymentArea = page()?.querySelector('[data-rwady-payment-area]');
    if (priceCard) priceCard.dataset.transferTheme = method || 'default';
    if (paymentArea) paymentArea.dataset.transferTheme = method || 'default';
    if (!box || !primary || !secondary) return;
    const isArabic = page()?.dir === 'rtl';
    const details = {
      qi_card: {
        primary: isArabic ? 'رقم بطاقة كي كارد: 4480750886' : 'Qi Card number: 4480750886',
        secondary: isArabic ? 'باسم: RAFID FALEH' : 'Name: RAFID FALEH'
      },
      zain_cash: { primary: isArabic ? 'التحويل على رقم الهاتف: 07833103331' : 'Transfer to phone number: 07833103331', secondary: isArabic ? 'باسم: RAFID FALEH' : 'Name: RAFID FALEH', copyValue: '07833103331' },
      fib: { primary: isArabic ? 'التحويل على رقم الهاتف: 07833103331' : 'Transfer to phone number: 07833103331', secondary: isArabic ? 'باسم: RAFID FALEH' : 'Name: RAFID FALEH', copyValue: '07833103331' },
      fast_pay: { primary: isArabic ? 'التحويل على رقم الهاتف: 07833103331' : 'Transfer to phone number: 07833103331', secondary: isArabic ? 'باسم: RAFID FALEH' : 'Name: RAFID FALEH', copyValue: '07833103331' }
    };
    const selected = details[method];
    box.hidden = !selected;
    if (!selected) return;
    primary.textContent = selected.primary;
    const copyValue = method === 'qi_card' ? '4480750886' : selected.copyValue;
    if (copyValue) {
      primary.dataset.copyValue = copyValue;
      primary.setAttribute('role', 'button');
      primary.setAttribute('tabindex', '0');
      primary.title = isArabic ? 'اضغط لنسخ الرقم' : 'Click to copy the number';
    } else {
      delete primary.dataset.copyValue;
      primary.removeAttribute('role');
      primary.removeAttribute('tabindex');
      primary.removeAttribute('title');
    }
    secondary.textContent = selected.secondary || '';
    secondary.hidden = !selected.secondary;
  };

  const restorePaymentPresentation = () => {
    const container = page();
    const payment = container?.querySelector('[data-rwady-field="payment"]');
    if (!container || !payment) return;
    const selected = payment.value || '';
    const paymentDetail = container.querySelector(`[data-payment-panel="${selected}"] [data-rwady-field="payment_detail"]`)?.value || '';
    container.querySelectorAll('[data-payment-panel]').forEach((panel) => {
      const active = panel.dataset.paymentPanel === selected;
      panel.hidden = !active;
      panel.querySelectorAll('select, input').forEach((field) => {
        field.disabled = !active;
        field.required = active;
      });
      const cardTrigger = panel.querySelector('[data-rwady-card-trigger]');
      if (cardTrigger) cardTrigger.disabled = !active;
    });
    updatePaymentTotals(selected, paymentDetail);
    updatePaymentTheme(payment);
    if (selected === 'electronic_transfer') updateTransferInformation(paymentDetail);
  };

  const restoreOrderConfirmationForm = () => {
    const container = page();
    if (!container) return false;
    let draft;
    try { draft = JSON.parse(sessionStorage.getItem('rwady-order-confirmation-draft-v1') || 'null'); } catch (_) { return false; }
    if (!draft || draft.version !== 1 || !draft.payload) return false;
    const fields = draft.form?.fields || {
      name: draft.payload.customer?.name || '',
      phone: draft.payload.customer?.phone || '',
      email: draft.payload.email || '',
      province: draft.payload.governorate || '',
      address: draft.payload.customer?.address || '',
      notes: draft.payload.customer?.notes || '',
      payment: draft.payload.payment_method || '',
      payment_detail: draft.payload.payment_detail || ''
    };
    const setValue = (selector, value) => {
      const field = container.querySelector(selector);
      if (!field || typeof value !== 'string') return;
      if (field.tagName === 'SELECT' && ![...field.options].some((option) => option.value === value)) return;
      field.value = value;
      const counter = field.closest('.rwady-precheckout__field')?.querySelector('small');
      if (counter && field.maxLength >= 0) counter.textContent = `${field.value.length}/${field.maxLength}`;
    };
    ['name', 'phone', 'email', 'province', 'address', 'notes', 'payment'].forEach((key) => setValue(`[data-rwady-field="${key}"]`, fields[key] || ''));
    setValue('[data-rwady-alternate-phone]', draft.form?.alternate_phone || draft.payload.customer?.phone2 || '');
    restorePaymentPresentation();
    setValue(`[data-payment-panel="${fields.payment || ''}"] [data-rwady-field="payment_detail"]`, fields.payment_detail || '');
    restorePaymentPresentation();
    const province = container.querySelector('[data-rwady-field="province"]');
    const provinceLabel = container.querySelector('[data-rwady-province-trigger-label]');
    if (provinceLabel && province?.selectedIndex >= 0) provinceLabel.textContent = province.options[province.selectedIndex].textContent.trim();
    container.querySelectorAll('[data-rwady-province-choice]').forEach((button) => button.classList.toggle('is-selected', button.dataset.rwadyProvinceChoice === province?.value));
    const payment = container.querySelector('[data-rwady-field="payment"]');
    const paymentLabel = container.querySelector('[data-rwady-payment-trigger-label]');
    if (paymentLabel && payment?.selectedIndex >= 0) paymentLabel.textContent = payment.options[payment.selectedIndex].textContent.trim();
    container.querySelectorAll('[data-rwady-payment-choice]').forEach((button) => button.classList.toggle('is-selected', button.dataset.rwadyPaymentChoice === payment?.value));
    const detail = container.querySelector(`[data-payment-panel="${payment?.value || ''}"] [data-rwady-field="payment_detail"]`);
    const detailPicker = detail?.closest('[data-rwady-card-picker], [data-rwady-transfer-picker]');
    const detailLabel = detailPicker?.querySelector('[data-rwady-card-trigger-label], [data-rwady-transfer-trigger-label]');
    if (detailLabel && detail?.selectedIndex >= 0) detailLabel.textContent = detail.options[detail.selectedIndex].textContent.trim();
    const location = container.querySelector('[data-rwady-location]');
    if (location && draft.form?.location) location.dataset.coordinates = draft.form.location;
    return true;
  };

  window.addEventListener('pageshow', () => { restorePaymentPresentation(); restoreOrderConfirmationForm(); });
  restorePaymentPresentation();
  restoreOrderConfirmationForm();

  const refreshSection = async (cart) => {
    const current = page();
    if (!current) return;
    const sectionId = current.dataset.sectionId;
    const response = await fetch(`${window.location.pathname}?section_id=${encodeURIComponent(sectionId)}`, { credentials: 'same-origin' });
    if (!response.ok) throw new Error('Unable to refresh checkout information');
    const html = await response.text();
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const replacement = documentFragment.querySelector('[data-rwady-checkout-information]');
    if (replacement) current.replaceWith(replacement);
    document.dispatchEvent(new CustomEvent('maximize:cart:updated', { detail: { cart } }));
  };

  document.addEventListener('input', (event) => {
    const field = event.target.closest('[data-rwady-field], [data-rwady-alternate-phone]');
    if (!field || !('maxLength' in field) || field.maxLength < 0) return;
    const counter = field.closest('.rwady-precheckout__field')?.querySelector('small');
    if (counter) counter.textContent = `${field.value.length}/${field.maxLength}`;
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-rwady-coupon-form]');
    if (!form) return;
    event.preventDefault();
    const input = form.querySelector('[data-rwady-coupon-code]');
    const error = form.querySelector('[data-rwady-coupon-error]');
    const code = input?.value.trim() || '';
    if (!code) {
      if (error) error.hidden = false;
      input?.focus();
      return;
    }
    if (error) error.hidden = true;
    const redirect = `${root()}pages/checkout-information`;
    window.location.assign(`${root()}discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(redirect)}`);
  });

  document.addEventListener('change', async (event) => {
    if (event.target.matches('[data-rwady-saved-address]')) {
      const option = event.target.options[event.target.selectedIndex];
      const picker = event.target.closest('[data-rwady-address-picker]');
      if (event.target.value && picker) {
        delete picker.dataset.addressSelectionRequired;
        const output = page()?.querySelector('[data-rwady-error]');
        if (output?.dataset.failureCode === 'ACCOUNT_SESSION_CHANGED' || output?.dataset.failureCode === 'ADDRESS_RESELECTION_REQUIRED') {
          output.hidden = true;
          output.textContent = '';
        }
      }
      const pickerLabel = picker?.querySelector('[data-rwady-address-trigger-label]');
      if (pickerLabel && option) pickerLabel.textContent = option.textContent.trim();
      picker?.querySelectorAll('[data-rwady-address-choice]').forEach((button) => button.classList.toggle('is-selected', button.dataset.rwadyAddressChoice === event.target.value));
      if (!option?.value) return;
      const container = page();
      const values = {
        name: option.dataset.name || '',
        phone: option.dataset.phone || '',
        email: option.dataset.email || '',
        province: option.dataset.province || '',
        address: savedAddressValue(option)
      };
      Object.entries(values).forEach(([key, value]) => {
        const field = container?.querySelector(`[data-rwady-field="${key}"]`);
        if (!field || !value) return;
        if (field.tagName === 'SELECT') {
          const match = Array.from(field.options).find((item) => item.value.toLowerCase() === value.toLowerCase() || item.textContent.trim().toLowerCase() === value.toLowerCase());
          if (match) field.value = match.value;
        } else {
          field.value = value;
        }
        field.dispatchEvent(new Event('input', { bubbles: true }));
        if (key === 'province') field.dispatchEvent(new Event('change', { bubbles: true }));
      });
      return;
    }
    if (event.target.matches('[data-rwady-transfer-receipt]')) {
      const file = event.target.files?.[0];
      const output = page()?.querySelector('[data-rwady-transfer-receipt-name]');
      if (!output) return;
      if (otpState.receiptUploading) {
        event.target.value = '';
        output.textContent = page()?.dir === 'rtl' ? 'انتظر اكتمال رفع الوصل الحالي' : 'Wait for the current receipt upload to finish';
        output.classList.add('is-error');
        return;
      }
      output.classList.remove('is-error', 'is-selected');
      if (!file) {
        otpState.receiptNeedsUpload = false;
        output.textContent = page()?.dir === 'rtl' ? 'صورة JPG أو PNG أو PDF — بحد أقصى 5MB' : 'JPG, PNG or PDF — maximum 5MB';
        return;
      }
      if (!validTransferReceipt(file)) {
        event.target.value = '';
        output.textContent = page()?.dir === 'rtl' ? 'الملف يجب أن يكون JPG أو PNG أو WebP أو PDF وبحجم لا يتجاوز 5MB' : 'Use a JPG, PNG, WebP or PDF file no larger than 5MB';
        output.classList.add('is-error');
        return;
      }
      otpState.receiptNeedsUpload = true;
      output.textContent = `${page()?.dir === 'rtl' ? 'تم اختيار الملف — سيُرفع بعد التحقق' : 'File selected — it will upload after verification'}: ${file.name}`;
      output.classList.add('is-selected');
      return;
    }
    if (event.target.matches('[data-rwady-field="payment"]')) {
      const selected = event.target.value;
      const previousReceiptAuthorization = otpState.authorization;
      const previousReceiptPhone = otpState.authorizationPhone;
      page()?.querySelectorAll('[data-payment-panel]').forEach((panel) => {
        const active = panel.dataset.paymentPanel === selected;
        panel.hidden = !active;
        panel.querySelectorAll('select, input').forEach((field) => {
          field.disabled = !active;
          field.required = active;
        });
        const cardTrigger = panel.querySelector('[data-rwady-card-trigger]');
        if (cardTrigger) cardTrigger.disabled = !active;
      });
      updatePaymentTotals(selected);
      updatePaymentTheme(event.target);
      if (selected !== 'electronic_transfer') {
        resetTransferReceiptSelection(page());
        if (previousReceiptAuthorization && previousReceiptPhone && (otpState.receiptUploadPromise || otpState.receiptStoredForAuthorization === previousReceiptAuthorization.pre_checkout_id)) {
          try {
            await unlinkTransferReceipt(previousReceiptAuthorization, previousReceiptPhone);
          } catch (error) {
            showCheckoutFailure(page()?.querySelector('[data-rwady-error]'), error);
          }
        }
      }
      return;
    }
    if (event.target.matches('[data-rwady-field="payment_detail"]') && !event.target.disabled) {
      const payment = page()?.querySelector('[data-rwady-field="payment"]')?.value || '';
      updatePaymentTotals(payment, event.target.value);
      if (event.target.matches('[data-rwady-transfer-method]')) {
        updateTransferInformation(event.target.value);
        page()?.querySelectorAll('[data-rwady-transfer-choice]').forEach((button) => {
          button.classList.toggle('is-selected', button.dataset.rwadyTransferChoice === event.target.value);
        });
      }
      return;
    }
    if (event.target.matches('[data-rwady-field="province"]')) {
      const picker = event.target.closest('[data-rwady-province-picker]');
      const selected = event.target.value;
      const label = picker?.querySelector('[data-rwady-province-trigger-label]');
      const option = event.target.options[event.target.selectedIndex];
      if (label && option) label.textContent = option.textContent.trim();
      picker?.querySelectorAll('[data-rwady-province-choice]').forEach((button) => button.classList.toggle('is-selected', button.dataset.rwadyProvinceChoice === selected));
      return;
    }
    if (!event.target.matches('[data-rwady-terms]')) return;
    const button = page()?.querySelector('[data-rwady-continue]');
    if (button && !otpState.checkoutStarted && !page()?.dataset.orderSubmitted) button.disabled = false;
  });

  document.addEventListener('click', async (event) => {
    const loginTrigger = event.target.closest('[data-rwady-login-trigger]');
    if (loginTrigger) {
      event.preventDefault();
      openLoginModal();
      return;
    }
    const loginAction = event.target.closest('[data-rwady-login-action]');
    if (loginAction) {
      event.preventDefault();
      if (loginAction.dataset.rwadyLoginAction === 'close') closeLoginModal();
      if (loginAction.dataset.rwadyLoginAction === 'send') await startCheckoutLogin();
      return;
    }
    const otpAction = event.target.closest('[data-rwady-otp-action]');
    if (otpAction) {
      const action = otpAction.dataset.rwadyOtpAction;
      if (action === 'verify') await verifyOtpAuthorization();
      if (action === 'resend' && !otpState.sending && !otpState.verifying) {
        otpState.sending = true;
        otpAction.disabled = true;
        try {
          const resendBody = otpState.mode === 'login'
            ? { phone: otpState.phone, intent: 'login', mode: 'login' }
            : { phone: otpState.phone, full_name: cleanCheckoutValue(page()?.querySelector('[data-rwady-field="name"]')?.value) };
          const sent = await accountClient().sendOtp(resendBody);
          if (!sent.challenge_id) throw checkoutFailure('otpSend', sent.code || 'OTP_SEND_REJECTED');
          otpState.challengeId = String(sent.challenge_id);
          setOtpStatus(document.documentElement.dir === 'rtl' ? 'تم إرسال رمز جديد.' : 'A new code was sent.', true);
        } catch (_) {
          setOtpStatus(document.documentElement.dir === 'rtl' ? 'تعذر إرسال رمز جديد الآن.' : 'Unable to send a new code now.');
        } finally {
          otpState.sending = false;
          otpState.resendTimer = window.setTimeout(() => { otpAction.disabled = false; otpState.resendTimer = 0; }, 60000);
        }
      }
      if (action === 'close') {
        const reject = otpState.reject;
        otpState.resolve = null;
        otpState.reject = null;
        closeOtpModal();
        if (reject) reject(checkoutFailure('otpVerify', 'OTP_CANCELLED'));
      }
      return;
    }
    const addressTrigger = event.target.closest('[data-rwady-address-trigger]');
    if (addressTrigger) {
      const menu = addressTrigger.closest('[data-rwady-address-picker]')?.querySelector('[data-rwady-address-menu]');
      if (menu) {
        menu.hidden = !menu.hidden;
        addressTrigger.setAttribute('aria-expanded', String(!menu.hidden));
      }
      return;
    }
    const addressChoice = event.target.closest('[data-rwady-address-choice]');
    if (addressChoice) {
      const picker = addressChoice.closest('[data-rwady-address-picker]');
      const select = picker?.querySelector('[data-rwady-saved-address]');
      if (select) {
        select.value = addressChoice.dataset.rwadyAddressChoice;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        const menu = picker.querySelector('[data-rwady-address-menu]');
        const trigger = picker.querySelector('[data-rwady-address-trigger]');
        if (menu) menu.hidden = true;
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
      }
      return;
    }
    const provinceTrigger = event.target.closest('[data-rwady-province-trigger]');
    if (provinceTrigger) {
      const menu = provinceTrigger.closest('[data-rwady-province-picker]')?.querySelector('[data-rwady-province-menu]');
      if (menu) {
        menu.hidden = !menu.hidden;
        provinceTrigger.setAttribute('aria-expanded', String(!menu.hidden));
      }
      return;
    }
    const provinceChoice = event.target.closest('[data-rwady-province-choice]');
    if (provinceChoice) {
      const picker = provinceChoice.closest('[data-rwady-province-picker]');
      const select = picker?.querySelector('[data-rwady-field="province"]');
      if (select) {
        select.value = provinceChoice.dataset.rwadyProvinceChoice;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        picker.querySelectorAll('[data-rwady-province-choice]').forEach((button) => button.classList.toggle('is-selected', button === provinceChoice));
        const label = picker.querySelector('[data-rwady-province-trigger-label]');
        const menu = picker.querySelector('[data-rwady-province-menu]');
        const trigger = picker.querySelector('[data-rwady-province-trigger]');
        if (label) label.textContent = provinceChoice.textContent.trim();
        if (menu) menu.hidden = true;
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
      }
      return;
    }
    const cardTrigger = event.target.closest('[data-rwady-card-trigger]');
    if (cardTrigger) {
      const menu = cardTrigger.closest('[data-rwady-card-picker]')?.querySelector('[data-rwady-card-menu]');
      if (menu) {
        menu.hidden = !menu.hidden;
        cardTrigger.setAttribute('aria-expanded', String(!menu.hidden));
      }
      return;
    }
    const cardChoice = event.target.closest('[data-rwady-card-choice]');
    if (cardChoice) {
      const picker = cardChoice.closest('[data-rwady-card-picker]');
      const select = picker?.querySelector('[data-rwady-field="payment_detail"]');
      if (select && !select.disabled) {
        select.value = cardChoice.dataset.rwadyCardChoice;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        picker.querySelectorAll('[data-rwady-card-choice]').forEach((button) => button.classList.toggle('is-selected', button === cardChoice));
        const label = picker.querySelector('[data-rwady-card-trigger-label]');
        const menu = picker.querySelector('[data-rwady-card-menu]');
        const trigger = picker.querySelector('[data-rwady-card-trigger]');
        if (label) label.textContent = cardChoice.textContent.trim();
        if (menu) menu.hidden = true;
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
      }
      return;
    }
    const paymentTrigger = event.target.closest('[data-rwady-payment-trigger]');
    if (paymentTrigger) {
      const menu = paymentTrigger.closest('[data-rwady-payment-picker]')?.querySelector('[data-rwady-payment-menu]');
      if (menu) {
        menu.hidden = !menu.hidden;
        paymentTrigger.setAttribute('aria-expanded', String(!menu.hidden));
      }
      return;
    }
    const paymentChoice = event.target.closest('[data-rwady-payment-choice]');
    if (paymentChoice) {
      const picker = paymentChoice.closest('[data-rwady-payment-picker]');
      const select = picker?.querySelector('[data-rwady-field="payment"]');
      if (select) {
        select.value = paymentChoice.dataset.rwadyPaymentChoice;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        picker.querySelectorAll('[data-rwady-payment-choice]').forEach((button) => button.classList.toggle('is-selected', button === paymentChoice));
        const label = picker.querySelector('[data-rwady-payment-trigger-label]');
        const menu = picker.querySelector('[data-rwady-payment-menu]');
        const trigger = picker.querySelector('[data-rwady-payment-trigger]');
        if (label) label.textContent = paymentChoice.textContent.trim();
        if (menu) menu.hidden = true;
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
      }
      return;
    }
    const transferTrigger = event.target.closest('[data-rwady-transfer-trigger]');
    if (transferTrigger) {
      const menu = transferTrigger.closest('[data-rwady-transfer-picker]')?.querySelector('[data-rwady-transfer-menu]');
      if (menu) {
        menu.hidden = !menu.hidden;
        transferTrigger.setAttribute('aria-expanded', String(!menu.hidden));
      }
      return;
    }
    const transferChoice = event.target.closest('[data-rwady-transfer-choice]');
    if (transferChoice) {
      const select = page()?.querySelector('[data-rwady-transfer-method]');
      if (select && !select.disabled) {
        select.value = transferChoice.dataset.rwadyTransferChoice;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        page()?.querySelectorAll('[data-rwady-transfer-choice]').forEach((button) => button.classList.toggle('is-selected', button === transferChoice));
        const picker = transferChoice.closest('[data-rwady-transfer-picker]');
        const label = picker?.querySelector('[data-rwady-transfer-trigger-label]');
        const menu = picker?.querySelector('[data-rwady-transfer-menu]');
        const trigger = picker?.querySelector('[data-rwady-transfer-trigger]');
        if (label) label.innerHTML = transferChoice.innerHTML;
        if (menu) menu.hidden = true;
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
      }
      return;
    }
    const copyTarget = event.target.closest('[data-copy-value]');
    if (copyTarget) {
      const value = copyTarget.dataset.copyValue;
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(value);
        } else {
          const helper = document.createElement('textarea');
          helper.value = value;
          helper.setAttribute('readonly', '');
          helper.style.position = 'fixed';
          helper.style.opacity = '0';
          document.body.appendChild(helper);
          helper.select();
          document.execCommand('copy');
          helper.remove();
        }
        const original = copyTarget.textContent;
        copyTarget.textContent = page()?.dir === 'rtl' ? 'تم نسخ الرقم ✓' : 'Number copied ✓';
        window.setTimeout(() => { copyTarget.textContent = original; }, 1400);
      } catch (error) {
        console.warn('[RWADY checkout information] Copy failed.', error);
      }
      return;
    }
    const removeButton = event.target.closest('[data-rwady-remove-item]');
    if (removeButton) {
      const item = removeButton.closest('[data-cart-item-key]');
      if (!item) return;
      removeButton.disabled = true;
      try {
        const response = await fetch(`${root()}cart/change.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: item.dataset.cartItemKey, quantity: 0 }) });
        if (!response.ok) throw new Error('Cart item removal failed');
        await refreshSection(await response.json());
      } catch (error) {
        removeButton.disabled = false;
        console.error('[RWADY checkout information]', error);
      }
      return;
    }

    const quantityButton = event.target.closest('[data-rwady-quantity]');
    if (quantityButton) {
      const item = quantityButton.closest('[data-cart-item-key]');
      const quantity = Number(item?.querySelector('.rwady-precheckout__qty span')?.textContent || 0);
      if (quantityButton.dataset.rwadyQuantity === 'minus' && quantity <= 1) return;
      const nextQuantity = Math.max(1, quantity + (quantityButton.dataset.rwadyQuantity === 'plus' ? 1 : -1));
      quantityButton.disabled = true;
      try {
        const response = await fetch(`${root()}cart/change.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: item.dataset.cartItemKey, quantity: nextQuantity }) });
        if (!response.ok) throw new Error('Cart update failed');
        await refreshSection(await response.json());
      } catch (error) {
        quantityButton.disabled = false;
        console.error('[RWADY checkout information]', error);
      }
      return;
    }

    const locationButton = event.target.closest('[data-rwady-location]');
    if (locationButton) {
      const status = page()?.querySelector('[data-rwady-location-status]');
      if (!navigator.geolocation) { if (status) status.textContent = 'Location is unavailable'; return; }
      locationButton.disabled = true;
      navigator.geolocation.getCurrentPosition((position) => {
        locationButton.dataset.coordinates = `${position.coords.latitude},${position.coords.longitude}`;
        if (status) status.textContent = document.documentElement.dir === 'rtl' ? 'تم تحديد الموقع' : 'Location captured';
        locationButton.disabled = false;
      }, () => { if (status) status.textContent = document.documentElement.dir === 'rtl' ? 'تعذر تحديد الموقع' : 'Unable to capture location'; locationButton.disabled = false; });
      return;
    }

    const continueButton = event.target.closest('[data-rwady-continue]');
    if (!continueButton) return;
    if (otpState.checkoutStarted || reviewBusy) return;
    const container = page();
    if (container.dataset.orderSubmitted) return;
    if (!markCheckoutFields(container, false)) {
      const output = container.querySelector('[data-rwady-error]');
      if (output) { output.textContent = !normalizedPhone(container.querySelector('[data-rwady-field="phone"]')?.value)
        ? checkoutPhoneError(container.querySelector('[data-rwady-field="phone"]')?.value)
        : nativeText('أكمل الحقول المحددة بالأحمر قبل مراجعة الطلب.', 'Complete the fields marked in red before reviewing the order.'); output.hidden = false; }
      return;
    }
    try { selectedPaymentMethod(container); } catch (exception) {
      showCheckoutFailure(container.querySelector('[data-rwady-error]'), exception);
      return;
    }
    const error = container.querySelector('[data-rwady-error]');
    otpState.checkoutStarted = true;
    continueButton.disabled = true;
    startOrderReviewTransition(container);
    try {
      const phone = window.RwadyAccountApi?.normalizePhone(container.querySelector('[data-rwady-field="phone"]')?.value);
      if (!phone) throw checkoutFailure('authorize', 'INVALID_PHONE');
      const authorization = await requestOtpAuthorization(phone, cleanCheckoutValue(container.querySelector('[data-rwady-field="name"]')?.value));
      await refreshAddressesAfterAuthorization(container, authorization);
      const contactPhone = authorization.account_access === true
        ? await persistAlternatePhone(container, phone)
        : checkoutAlternatePhone(container, phone);
      await persistInitialRegistrationAddress(container, authorization, contactPhone);
      if (selectedPaymentMethod(container) === 'electronic_transfer') {
        await persistTransferReceipt(container, authorization, phone);
      } else {
        await unlinkTransferReceipt(authorization, phone);
      }
      const attributes = {};
      container.querySelectorAll('[data-rwady-field]').forEach((field) => {
        if (field.disabled) return;
        const value = field.type === 'file' ? field.files?.[0]?.name || '' : field.value.trim();
        attributes[`Delivery ${field.dataset.rwadyField}`] = value;
      });
      appendStandardPaymentAttributes(container, attributes);
      const loyaltyCartResponse = await fetch(`${root()}cart.js`, { cache: 'no-store' });
      if (!loyaltyCartResponse.ok) throw checkoutFailure('loyalty', 'CART_READ_FAILED');
      const loyaltyCart = await loyaltyCartResponse.json();
      const loyalty = await window.RwadyLoyalty?.prepareCheckout(loyaltyCart);
      if (loyalty && !authorization.account_access) throw checkoutFailure('loyalty', 'LOYALTY_ACCOUNT_PHONE_MISMATCH');
      attributes.rwady_loyalty_redemption_id = loyalty?.id || '';
      appendPaymentPricingAttributes(container, attributes, loyalty?.value_iqd || 0);
      attributes.pre_checkout_id = authorization.pre_checkout_id;
      const location = container.querySelector('[data-rwady-location]')?.dataset.coordinates;
      if (location) attributes['Delivery location'] = location;
      const response = await fetch(`${root()}cart/update.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ attributes }) });
      const contentType = response.headers.get('content-type') || '';
      let updatedCart;
      try { updatedCart = await response.json(); } catch (_) {
        throw checkoutFailure('cartUpdate', 'CART_UPDATE_INVALID_JSON', { status: response.status, contentType, jsonValid: false });
      }
      if (!response.ok) throw checkoutFailure('cartUpdate', 'CART_UPDATE_HTTP_FAILED', { status: response.status, contentType, jsonValid: true });
      const expectedAttributes = Object.fromEntries(Object.entries(attributes).filter(([, value]) => value !== ''));
      if (!window.RwadyStorefrontCart.hasExpectedAttributes(
        Object.entries(updatedCart.attributes || {}).map(([key, value]) => ({ key, value })),
        expectedAttributes
      )) throw checkoutFailure('cartUpdate', 'CART_UPDATE_ATTRIBUTES_MISMATCH', { status: response.status, contentType, jsonValid: true });
      if (window.RwadyAccountApi.normalizePhone(container.querySelector('[data-rwady-field="phone"]')?.value) !== phone) throw checkoutFailure('authorize', 'PHONE_CHANGED_AFTER_VERIFICATION');
      const customer = storefrontCustomer(container, contactPhone);
      const payload = nativePayload(updatedCart, customer, authorization, container, loyalty);
      const result = await nativeClient().request('prepare', payload, authorization);
      if (result.status === 'submitted') { showConfirmedOrder(result, payload.payment_method, payload, updatedCart); goToThankYou(); return; }
      saveOrderConfirmationDraft(result, payload, authorization, updatedCart);
      await trackAddPaymentInfo(result, payload);
      goToOrderConfirmation();
    } catch (exception) {
      stopOrderReviewTransition(container);
      if (exception?.code === 'INVALID_AUTHORIZATION') otpState.authorization = null;
      showCheckoutFailure(error, exception);
      continueButton.disabled = false;
      otpState.checkoutStarted = false;
    }
  });

  document.addEventListener('click', (event) => {
    if (paymentPromptEvents.has(event)) return;
    if (event.target.closest('[data-rwady-transfer-picker],[data-rwady-payment-picker],[data-rwady-card-picker],[data-rwady-province-picker],[data-rwady-address-picker]')) return;
    page()?.querySelectorAll('[data-rwady-transfer-menu]').forEach((menu) => { menu.hidden = true; });
    page()?.querySelectorAll('[data-rwady-transfer-trigger]').forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
    page()?.querySelectorAll('[data-rwady-payment-menu]').forEach((menu) => { menu.hidden = true; });
    page()?.querySelectorAll('[data-rwady-payment-trigger]').forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
    page()?.querySelectorAll('[data-rwady-card-menu]').forEach((menu) => { menu.hidden = true; });
    page()?.querySelectorAll('[data-rwady-card-trigger]').forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
    page()?.querySelectorAll('[data-rwady-province-menu]').forEach((menu) => { menu.hidden = true; });
    page()?.querySelectorAll('[data-rwady-province-trigger]').forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
    page()?.querySelectorAll('[data-rwady-address-menu]').forEach((menu) => { menu.hidden = true; });
    page()?.querySelectorAll('[data-rwady-address-trigger]').forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
  });

  document.addEventListener('keydown', (event) => {
    if ((event.key === 'Enter' || event.key === ' ') && event.target.matches('[data-copy-value]')) {
      event.preventDefault();
      event.target.click();
    }
  });
})();

/* RWADY delivery review notice live sync 2026-09-28 */
